import type { AuthenticatedUser, ThemePreference } from '@livechat/shared';
import { useEffect, useRef, useState } from 'react';

import { updateThemePreference } from '../api.js';
import { applyThemePreference } from '../theme.js';

const OPTIONS: { label: string; value: ThemePreference }[] = [
	{ label: 'Light', value: 'light' },
	{ label: 'Dark', value: 'dark' },
	{ label: 'System', value: 'system' },
];

function ThemeIcon({ preference }: { preference: ThemePreference }) {
	if (preference === 'dark') {
		return (
			<svg aria-hidden="true" viewBox="0 0 20 20">
				<path d="M16.4 12.8A7 7 0 0 1 7.2 3.6 7 7 0 1 0 16.4 12.8Z" />
				<path d="m13.8 3 .4 1.1 1.1.4-1.1.4-.4 1.1-.4-1.1-1.1-.4 1.1-.4.4-1.1Z" />
			</svg>
		);
	}
	if (preference === 'system') {
		return (
			<svg aria-hidden="true" viewBox="0 0 20 20">
				<circle cx="10" cy="10" r="6.5" />
				<path d="M10 3.5v13" />
			</svg>
		);
	}
	return (
		<svg aria-hidden="true" viewBox="0 0 20 20">
			<circle cx="10" cy="10" r="3.25" />
			<path d="M10 1.5v2M10 16.5v2M1.5 10h2M16.5 10h2M4 4l1.4 1.4M14.6 14.6 16 16M16 4l-1.4 1.4M5.4 14.6 4 16" />
		</svg>
	);
}

export function ThemeSelector({
	onChanged,
	preference,
}: {
	onChanged: (user: AuthenticatedUser) => void;
	preference: ThemePreference;
}) {
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string>();
	const [open, setOpen] = useState(false);
	const controlRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;
		function close(event: MouseEvent): void {
			if (!controlRef.current?.contains(event.target as Node)) setOpen(false);
		}
		function closeOnEscape(event: KeyboardEvent): void {
			if (event.key === 'Escape') setOpen(false);
		}
		document.addEventListener('mousedown', close);
		document.addEventListener('keydown', closeOnEscape);
		return () => {
			document.removeEventListener('mousedown', close);
			document.removeEventListener('keydown', closeOnEscape);
		};
	}, [open]);

	async function select(next: ThemePreference): Promise<void> {
		setOpen(false);
		if (next === preference || busy) return;
		setBusy(true);
		setError(undefined);
		applyThemePreference(next);
		try {
			onChanged(await updateThemePreference(next));
		} catch (caught) {
			applyThemePreference(preference);
			setError(caught instanceof Error ? caught.message : 'Could not save appearance');
		} finally {
			setBusy(false);
		}
	}
	const label = OPTIONS.find((option) => option.value === preference)?.label ?? 'System';

	return (
		<div className="theme-control-wrap" ref={controlRef}>
			<button
				aria-expanded={open}
				aria-haspopup="menu"
				aria-label={`Interface appearance: ${label}`}
				className="theme-menu-toggle"
				disabled={busy}
				onClick={() => setOpen((current) => !current)}
				title={`Interface appearance: ${label}`}
				type="button"
			>
				<ThemeIcon preference={preference} />
			</button>
			{open && (
				<div aria-label="Interface appearance" className="theme-menu" role="menu">
					{OPTIONS.map((option) => (
						<button
							aria-checked={preference === option.value}
							className={preference === option.value ? 'selected' : ''}
							key={option.value}
							onClick={() => void select(option.value)}
							role="menuitemradio"
							type="button"
						>
							<ThemeIcon preference={option.value} />
							<span>{option.label}</span>
						</button>
					))}
				</div>
			)}
			{error && (
				<span className="theme-error" role="alert">
					{error}
				</span>
			)}
		</div>
	);
}
